/**
 * VisitorTracker Component
 * Automatically tracks page views across the app
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('afriqoma_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('afriqoma_session_id', sessionId);
  }
  return sessionId;
};

// Get UTM params
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || ''
  };
};

// Get device type
const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod/.test(ua)) return 'mobile';
  if (/tablet|ipad/.test(ua)) return 'tablet';
  return 'desktop';
};

// Get country from timezone
const getCountry = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const map = {
      'Africa/Lagos': 'Nigeria', 'Africa/Johannesburg': 'South Africa',
      'Africa/Nairobi': 'Kenya', 'Africa/Accra': 'Ghana', 'Africa/Cairo': 'Egypt',
      'Europe/London': 'UK', 'America/New_York': 'USA', 'Europe/Paris': 'France'
    };
    return map[tz] || tz.split('/')[0];
  } catch { return ''; }
};

// Track event silently
const track = (data) => {
  try {
    const payload = {
      session_id: getSessionId(),
      ...data,
      ...getUTMParams(),
      device_type: getDeviceType(),
      country: getCountry(),
      referrer: document.referrer || ''
    };
    
    // Use sendBeacon for reliability, fallback to fetch
    const url = `${API_URL}/api/analytics/track`;
    const body = JSON.stringify(payload);
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, body);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {});
    }
  } catch (e) {
    // Non-critical visitor tracking
    if (process.env.NODE_ENV === 'development') console.debug('Visitor tracking skipped:', e.message);
  }
};

export const VisitorTracker = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view
    const startTime = Date.now();
    let maxScroll = 0;
    
    track({
      page: location.pathname,
      event_type: 'page_view'
    });
    
    // Track scroll depth
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      maxScroll = Math.max(maxScroll, scrollPercent);
    };
    
    // Track page exit
    const handleUnload = () => {
      track({
        page: location.pathname,
        event_type: 'page_exit',
        duration: Math.round((Date.now() - startTime) / 1000),
        scroll_depth: maxScroll
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleUnload);
      // Track exit on route change
      track({
        page: location.pathname,
        event_type: 'page_exit',
        duration: Math.round((Date.now() - startTime) / 1000),
        scroll_depth: maxScroll
      });
    };
  }, [location.pathname]);
  
  return children;
};

export default VisitorTracker;
